import {
  type ComponentType, type FunctionComponent, type ReactNode, useState,
} from 'react';
import {
  Link,
} from '@dr.pogodin/react-utils';

import Avatar from 'components/Avatar';
import { useArtists, useRankingUserScores } from 'hooks';
import type { RankingUserScoreKindT, SessionT, UserScoreT } from 'utils';

// import IconArrowRight from 'assets/images/ai/arrow-right.svg';
import IconBreathwork from 'assets/images/icon-profile-breathwork.svg';
import IconCallistenic from 'assets/images/icon-profile-callistenic.svg';
import IconFunctional from 'assets/images/icon-profile-functional.svg';
import IconMeditation from 'assets/images/icon-profile-meditation.svg';
import IconOther from 'assets/images/icon-profile-other.svg';
import IconPilates from 'assets/images/icon-profile-pilates.svg';
import IconSomatic from 'assets/images/icon-profile-somatic.svg';
import IconSoundbath from 'assets/images/icon-profile-soundbath.svg';
import IconTaichi from 'assets/images/icon-profile-taichi.svg';
import IconYoga from 'assets/images/icon-profile-yoga.svg';

import './style.scss';

const MODALITY_ICONS: Record<string, ComponentType> = {
  Breathwork: IconBreathwork,
  Calisthenics: IconCallistenic,
  'Functional Movement': IconFunctional,
  Meditation: IconMeditation,
  Other: IconOther,
  Pilates: IconPilates,
  'QiGong/Tai Chi': IconTaichi,
  'Somatic Movement': IconSomatic,
  Soundbath: IconSoundbath,
  Yoga: IconYoga,
};

const ARTIST_TABS = ['Most Experienced', 'Highest Impact'] as const;
const MODALITY_TABS = ['Most Used', 'Highest Impact'] as const;

const ARTIST_TAB_KIND: Record<
  typeof ARTIST_TABS[number], RankingUserScoreKindT
> = {
  'Highest Impact': 'ARTIST_IMPACT_COUNT',
  'Most Experienced': 'ARTIST_EXPERIENCE_COUNT',
};

const MODALITY_TAB_KIND: Record<
  typeof MODALITY_TABS[number], RankingUserScoreKindT
> = {
  'Highest Impact': 'MODALITY_IMPACT_COUNT',
  'Most Used': 'MODALITY_EXPERIENCE_COUNT',
};

const MAX_LEADERBOARD_ITEMS = 5;

function sortRanking(
  score: UserScoreT | undefined,
): Array<[string, number]> {
  const ranking = Array.isArray(score?.value) ? score.value : [];
  return [...ranking]
    .sort((a, b) => b[1] - a[1])
    .slice(0, MAX_LEADERBOARD_ITEMS);
}

type PropsT = {
  children?: ReactNode;
  loading?: boolean;
  sessions: SessionT[];
  title: string;
};

const RightCol: FunctionComponent<PropsT> = ({
  children,
  title,
}) => {
  const [activeTab, setActiveTab] = useState<typeof ARTIST_TABS[number]>(
    'Most Experienced',
  );
  const [modalityTab, setModalityTab] = useState<
    typeof MODALITY_TABS[number]
  >('Most Used');

  const { scores: rankingScores } = useRankingUserScores();

  const artistRanking = sortRanking(rankingScores[ARTIST_TAB_KIND[activeTab]]);
  const artistIds = artistRanking.map(([artistId]) => artistId);
  const { items: artistItems } = useArtists(artistIds);

  const modalityRanking = sortRanking(
    rankingScores[MODALITY_TAB_KIND[modalityTab]],
  );
  const maxModalityCount = modalityRanking[0]?.[1] ?? 0;

  const artistCountLabel = activeTab === 'Highest Impact'
    ? 'impact mentions'
    : 'experiences';

  return (
    <div styleName="rightCol">
      <div styleName="header">
        <h1>{title}</h1>
      </div>
      <div styleName="favoriteArtistsContainer">
        <div styleName="sectionHeader">
          <h3>Favorite Artists</h3>
          {/* <div styleName="viewAll">
            <span>View all artist</span>
            <IconArrowRight />
          </div> */}
        </div>

        <div styleName="favoriteArtists">
          <div styleName="artistList">
            <div styleName="tabsContainer">
              <div styleName="tabs">
                {ARTIST_TABS.map((tab) => (
                  <Link
                    key={tab}
                    onClick={(event) => {
                      event.preventDefault();
                      setActiveTab(tab);
                    }}
                    styleName={`tab ${activeTab === tab ? 'active' : ''}`}
                    to="#"
                  >
                    {tab}
                  </Link>
                ))}
              </div>
            </div>

            {artistRanking.length === 0 ? (
              <div styleName="emptyState">
                No artist data available yet.
              </div>
            ) : null}

            {artistRanking.map(([artistId, count], index) => {
              const artist = artistItems[artistId]?.data;
              const fullName = [
                artist?.user.firstName,
                artist?.user.lastName,
              ].filter(Boolean).join(' ');
              let name = 'Unknown Practitioner';
              if (artist?.displayName) name = artist.displayName;
              else if (fullName) name = fullName;

              return (
                <div key={artistId} styleName="artistRow">
                  <div styleName="artistRank">{index + 1}</div>
                  <div styleName="artistInfo">
                    <Avatar
                      size={40}
                      user={artist?.user ?? { firstName: '', id: '', lastName: '' }}
                    />
                    <div styleName="artistName">{name}</div>
                  </div>
                  <div styleName="artistExp">
                    <b>{count}</b>
                    {' '}
                    {artistCountLabel}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div styleName="modalityProfileContainer">
        <div styleName="sectionHeader">
          <h3>Modality Profile</h3>
          {/* <div styleName="viewAll">
            <span>View all modality</span>
            <IconArrowRight />
          </div> */}
        </div>

        <div styleName="modalityProfile">
          <div styleName="modalityList">
            <div styleName="tabsContainer">
              <div styleName="tabs">
                {MODALITY_TABS.map((tab) => (
                  <Link
                    key={tab}
                    onClick={(event) => {
                      event.preventDefault();
                      setModalityTab(tab);
                    }}
                    styleName={`tab ${modalityTab === tab ? 'active' : ''}`}
                    to="#"
                  >
                    {tab}
                  </Link>
                ))}
              </div>
            </div>

            {modalityRanking.length === 0 ? (
              <div styleName="emptyState">
                No modality data available yet.
              </div>
            ) : null}

            {modalityRanking.map(([name, count]) => {
              const Icon = MODALITY_ICONS[name] ?? IconOther;
              const percentage = maxModalityCount > 0
                ? Math.round((count / maxModalityCount) * 100)
                : 0;

              return (
                <div key={name} styleName="modalityRow">
                  <div styleName="modalityPercentage">
                    {percentage}
                    %
                  </div>
                  <div styleName="modalityIcon">
                    <Icon />
                  </div>
                  <div styleName="modalityTitle">{name}</div>
                  <div styleName="progressBar">
                    <div
                      style={{ width: `${percentage}%` }}
                      styleName="progress"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {children}
    </div>
  );
};

export default RightCol;
